import { Modal, SubmitButton, Switch } from "@xola/ui-kit";
import { FormField, InputField, requiredString, SelectField } from "common";
import React from "react";
import { TelemetryModuleParameter, TelemetryParameterType } from "types";
import { object, string } from "yup";
import { ScrollToFieldError } from "../../helpers/validationHelper";
import { useVisibility } from "../../hooks/useVisibility";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";

interface TelemetryModuleParameterModalProps {
    isOpen: boolean;
    parameter: TelemetryModuleParameter | null;
    onClose: () => void;
    onApply: (values: TelemetryModuleParameter) => void;
}

export const TelemetryModuleParameterModal = ({
    onClose,
    parameter,
    isOpen,
    onApply,
}: TelemetryModuleParameterModalProps) => {
    const handleClose = () => {
        onClose();
    };

    return (
        <Modal isOpen={isOpen} size="large" onClose={handleClose}>
            <ModalContent parameter={parameter} onClose={onClose} onApply={onApply} />
        </Modal>
    );
};

interface ModalContentProps {
    parameter: TelemetryModuleParameter | null;
    onClose: () => void;
    onApply: (values: TelemetryModuleParameter) => void;
}

const validationSchema = object().shape({
    description: string(),
    key: requiredString(),
    type: requiredString(),
    value: string(),
    label: requiredString(),
});

const ModalContent = ({ parameter, onClose, onApply }: ModalContentProps) => {
    const [showAdvancedOptions, { toggle: toggleAdvancedOptions }] = useVisibility(false);

    const handleSubmit = (values: TelemetryModuleParameter) => {
        onApply(values);
        onClose();
    };

    const initialValues = parameter ?? {
        type: TelemetryParameterType.QUERYSTRING,
        key: "",
        label: "",
        caption: "",
        retry: 10,
    };

    return (
        <XolaFormik
            validateOnChange={false}
            validationSchema={validationSchema}
            initialValues={initialValues}
            onSubmit={handleSubmit}
        >
            {({ values, handleSubmit }) => (
                <>
                    <Modal.Header>Add Parameter</Modal.Header>

                    <Modal.Body>
                        <div className="space-y-4">
                            <FormField name="type" label="Type">
                                <SelectField className="leading-p1">
                                    <option value={TelemetryParameterType.STRING}>Fixed String</option>
                                    <option value={TelemetryParameterType.QUERYSTRING}>Query String</option>
                                    <option value={TelemetryParameterType.BOOLEAN}>Boolean</option>
                                    <option value={TelemetryParameterType.FUNCTION}>Function</option>
                                    <option value={TelemetryParameterType.SECRET}>Secret</option>
                                    <option value={TelemetryParameterType.COOKIE}>Cookie</option>
                                    <option value={TelemetryParameterType.PROPERTY}>Window Object Property</option>
                                </SelectField>
                            </FormField>
                            <FormField
                                name="key"
                                label={
                                    values.type === TelemetryParameterType.FUNCTION
                                        ? "Function (must be callable on your website)"
                                        : "Key"
                                }
                            >
                                <InputField isRequired value={values.key} />
                            </FormField>
                            <FormField name="label" label="Label">
                                <InputField value={values.label} />
                            </FormField>
                            {values.type === TelemetryParameterType.STRING ||
                            values.type === TelemetryParameterType.SECRET ? (
                                <FormField name="value" label="Value">
                                    <InputField
                                        type={values.type === TelemetryParameterType.SECRET ? "password" : "text"}
                                        value={values.value}
                                    />
                                </FormField>
                            ) : null}
                            <FormField name="description" label="Description">
                                <InputField value={values.caption} />
                            </FormField>

                            <Switch.Group>
                                <Switch isChecked={showAdvancedOptions} size="large" onChange={toggleAdvancedOptions} />

                                <Switch.Label direction="left" className="ml-3">
                                    Advanced Settings
                                </Switch.Label>
                            </Switch.Group>

                            {showAdvancedOptions && (
                                <div className="space-y-4">
                                    <FormField name="pattern" label="Pattern">
                                        <InputField value={values.pattern} />
                                    </FormField>
                                    <FormField name="retry" label="Retry">
                                        <InputField type="number" value={values.retry} />
                                    </FormField>
                                </div>
                            )}
                        </div>
                    </Modal.Body>

                    <Modal.Footer className="space-x-4">
                        <SecondaryButton data-testid="telmetry-parameter-editor-modal-cancel-button" onClick={onClose}>
                            Cancel
                        </SecondaryButton>

                        <SubmitButton data-testid="telmetry-parameter-editor-modal-apply-button" onClick={handleSubmit}>
                            Apply
                        </SubmitButton>
                    </Modal.Footer>

                    <ScrollToFieldError />
                </>
            )}
        </XolaFormik>
    );
};
